import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TablePagination,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDispatch, useSelector } from "react-redux";
import {
  getPaymentsAction,
  setPaymentOffset,
  setPaymentMonthYear,
  setPaymentModuleFilter,
} from "../../redux/actions/payment";
import Chip from "@mui/material/Chip";
import strings from "../../i18n/language";
import dayjs from "dayjs";

const getTypeLabel = (type) => {
  switch (type) {
    case "efectivo":
      return strings.LABEL_CASH;
    case "transferencia":
      return strings.LABEL_TRANSFER;
    case "pago_movil":
      return strings.LABEL_MOBILE_PAYMENT;
    default:
      return type;
  }
};

const getOwnerName = (payment) => {
  const subscription = payment?.Subscription;
  if (!subscription) return "";
  if (subscription?.Company?.name) return subscription.Company.name;
  const patient =
    subscription?.PatientSubscriptions?.[0]?.Patient;
  return patient?.displayName || "";
};

const PaymentList = () => {
  const dispatch = useDispatch();
  const payments = useSelector((state) => state.payment.payments);
  const total = useSelector((state) => state.payment.total);
  const totals = useSelector((state) => state.payment.totals);
  const limit = useSelector((state) => state.payment.limit);
  const month = useSelector((state) => state.payment.month);
  const year = useSelector((state) => state.payment.year);
  const moduleFilter = useSelector((state) => state.payment.moduleFilter);
  const isLoading = useSelector(
    (state) => state.payment.isLoadingGetPayments
  );

  const [page, setPage] = useState(0);

  useEffect(() => {
    dispatch(getPaymentsAction());
  }, [dispatch]);

  const handlePrevMonth = () => {
    let newMonth = month - 1;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear = year - 1;
    }
    setPage(0);
    dispatch(setPaymentMonthYear(newMonth, newYear));
  };

  const handleNextMonth = () => {
    let newMonth = month + 1;
    let newYear = year;
    if (newMonth > 12) {
      newMonth = 1;
      newYear = year + 1;
    }
    setPage(0);
    dispatch(setPaymentMonthYear(newMonth, newYear));
  };

  const monthLabel = dayjs(`${year}-${month}-01`).format("MMMM YYYY");

  const usdTotal = totals?.find((t) => t.currency === "USD");
  const bolTotal = totals?.find((t) => t.currency === "BOL");
  const totalCount = totals?.reduce(
    (acc, t) => acc + parseInt(t.count || 0),
    0
  );

  return (
    <Paper sx={{ p: 2, width: "80%" }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            width: "100%",
          }}
        />
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ mb: 2 }}
      >
        <IconButton onClick={handlePrevMonth}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ mx: 2, textTransform: "capitalize" }}>
          {monthLabel}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box display="flex" gap={1} sx={{ mb: 2 }}>
        {["Servital", "Laboral"].map((mod) => (
          <Chip
            key={mod}
            label={mod}
            color={moduleFilter === mod ? "primary" : "default"}
            variant={moduleFilter === mod ? "filled" : "outlined"}
            onClick={() => dispatch(setPaymentModuleFilter(moduleFilter === mod ? null : mod))}
            clickable
          />
        ))}
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 1, "&:last-child": { pb: 1 } }}>
              <Typography variant="body2" color="text.secondary">
                {strings.LABEL_TOTAL_PAYMENTS}
              </Typography>
              <Typography variant="h5">{totalCount || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 1, "&:last-child": { pb: 1 } }}>
              <Typography variant="body2" color="text.secondary">
                {strings.LABEL_TOTAL_USD}
              </Typography>
              <Typography variant="h5">
                ${parseFloat(usdTotal?.totalAmount || 0).toLocaleString("es", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 1, "&:last-child": { pb: 1 } }}>
              <Typography variant="body2" color="text.secondary">
                {strings.LABEL_TOTAL_BOL}
              </Typography>
              <Typography variant="h5">
                Bs.{parseFloat(bolTotal?.totalAmount || 0).toLocaleString("es", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {payments && payments.length > 0 ? (
        <TableContainer>
          <Table aria-label="Payments table">
            <TableHead>
              <TableRow>
                <TableCell>{strings.LABEL_DATE}</TableCell>
                <TableCell>{strings.LABEL_TYPE}</TableCell>
                <TableCell>{strings.LABEL_CURRENCY}</TableCell>
                <TableCell align="right">{strings.LABEL_AMOUNT}</TableCell>
                <TableCell>{strings.LABEL_SUBSCRIPTION_ID}</TableCell>
                <TableCell>{strings.LABEL_PATIENT_COMPANY}</TableCell>
                <TableCell>Módulo</TableCell>
                <TableCell>{strings.LABEL_COMMENT}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{dayjs(payment.date).format("L")}</TableCell>
                  <TableCell>{getTypeLabel(payment.type)}</TableCell>
                  <TableCell>{payment.currency}</TableCell>
                  <TableCell align="right">{payment.amount}</TableCell>
                  <TableCell>{payment.subscription_id}</TableCell>
                  <TableCell>{getOwnerName(payment)}</TableCell>
                  <TableCell>{payment.module || "Servital"}</TableCell>
                  <TableCell>{payment.comment}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[limit]}
            count={total}
            rowsPerPage={limit}
            page={page}
            onPageChange={(event, newPage) => {
              dispatch(setPaymentOffset(limit * newPage));
              setPage(newPage);
            }}
            component="div"
          />
        </TableContainer>
      ) : (
        !isLoading && (
          <Typography
            sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
          >
            {strings.MESSAGE_NO_PAYMENTS_THIS_MONTH}
          </Typography>
        )
      )}
    </Paper>
  );
};

export default PaymentList;
